import { ChevronDown, ChevronUp, Info } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { Tooltip } from "@/shared/ui/tooltip";

import type { DiffFilterState, DiffRow, DiffSortKey } from "../lib/diff-filter";
import { formatPct } from "../lib/diff-format";

import { HealthBar } from "./HealthBar";
import { Sparkline } from "./Sparkline";

interface DiffTableProps {
  rows: DiffRow[];
  filter: DiffFilterState;
  onSort: (key: DiffSortKey) => void;
  onRowClick: (sku: string) => void;
}

const COLS: { key: DiffSortKey | "trend"; sortable: boolean }[] = [
  { key: "sku", sortable: true },
  { key: "expect_quantity", sortable: true },
  { key: "real_quantity", sortable: true },
  { key: "diff", sortable: true },
  { key: "pct", sortable: true },
  { key: "trend", sortable: false },
  { key: "snapshot_at", sortable: true },
];
const LABEL: Record<string, string> = {
  sku: "column.sku",
  expect_quantity: "column.expect",
  real_quantity: "column.real",
  diff: "column.diff",
  pct: "column.pct",
  trend: "column.trend",
  snapshot_at: "column.created",
};
// Only the three columns whose MEANING changed in the 2026-09-19 redesign
// carry a tooltip. Expect is no longer "what Atlas last told itself", Real is
// no longer "now", and Snapshot is the report time, not the 06:00 detector
// run — without this, the page reads exactly as it did before.
const TOOLTIP: Partial<Record<string, string>> = {
  expect_quantity: "tooltip.expect",
  real_quantity: "tooltip.real",
  snapshot_at: "tooltip.snapshot",
};

export function DiffTable({ rows, filter, onSort, onRowClick }: DiffTableProps): JSX.Element {
  const { t, i18n } = useTranslation("inventory_diff");
  const nf = new Intl.NumberFormat(i18n.language);
  return (
    <div className="overflow-x-auto rounded-[14px] border border-[var(--premium-border)] shadow-[var(--sh-card)]">
      <table className="min-w-full border-collapse" data-testid="diff-table">
        <thead className="border-b border-[var(--row-divider)] bg-white">
          <tr>
            {COLS.map((c) => {
              const active = c.sortable && filter.sortKey === c.key;
              return (
                <th
                  key={c.key}
                  onClick={c.sortable ? () => onSort(c.key as DiffSortKey) : undefined}
                  data-testid={`diff-th-${c.key}`}
                  className={`h-11 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap ${c.sortable ? "cursor-pointer select-none" : ""}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {t(LABEL[c.key]!)}
                    {TOOLTIP[c.key] && (
                      <Tooltip content={t(TOOLTIP[c.key]!)}>
                        <button
                          type="button"
                          data-testid={`diff-tip-${c.key}`}
                          aria-label={t(TOOLTIP[c.key]!)}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex cursor-help border-0 bg-none p-0 text-[var(--text-subtle)] focus-ring"
                        >
                          <Info className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </Tooltip>
                    )}
                    {active &&
                      (filter.sortDir === "asc" ? (
                        <ChevronUp className="h-3.5 w-3.5" aria-hidden />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                      ))}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--row-divider)]">
          {rows.map((r) => {
            const quiet = r.diff === 0;
            return (
              <tr
                key={r.sku}
                data-testid={`diff-row-${r.sku}`}
                data-over={r.over_threshold ? "true" : "false"}
                onClick={() => onRowClick(r.sku)}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(r.sku);
                  }
                }}
                className={`cursor-pointer focus-ring ${r.over_threshold ? "bg-[var(--intent-danger-bg)]" : "bg-white"}`}
              >
                <td
                  className={`sku px-4 py-2.5 text-[13px] ${quiet ? "text-[var(--text-subtle)]" : "text-[var(--text-primary)]"}`}
                >
                  {r.sku}
                </td>
                <td
                  className={`sku px-4 py-2.5 text-right text-[13px] ${quiet ? "text-[var(--text-subtle)]" : "text-[var(--text-primary)]"}`}
                >
                  {nf.format(r.expect_quantity)}
                </td>
                <td
                  className={`sku px-4 py-2.5 text-right text-[13px] ${quiet ? "text-[var(--text-subtle)]" : "text-[var(--text-primary)]"}`}
                >
                  {nf.format(r.real_quantity)}
                </td>
                <td
                  className={`sku px-4 py-2.5 text-right text-[13px] font-semibold ${r.over_threshold ? "text-[var(--intent-danger-fg)]" : quiet ? "text-[var(--text-subtle)]" : "text-[var(--text-primary)]"}`}
                >
                  {r.diff > 0 ? `+${nf.format(r.diff)}` : nf.format(r.diff)}
                </td>
                <td
                  data-testid={`diff-pct-${r.sku}`}
                  className={`px-4 py-2.5 text-right text-[13px] ${r.over_threshold ? "text-[var(--intent-danger-fg)]" : "text-[var(--text-secondary)]"}`}
                >
                  <span className="inline-flex items-center gap-2">
                    <HealthBar pct={r.pct} over={r.over_threshold} />
                    {formatPct(r.pct)}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  {r.trend.length > 0 ? (
                    <Sparkline
                      trend={r.trend}
                      over={r.over_threshold}
                      testId={`diff-spark-${r.sku}`}
                    />
                  ) : (
                    <span className="text-[var(--text-subtle)]">{t("state.none")}</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-[13px] text-[var(--text-secondary)]">
                  {new Date(r.snapshot_at).toLocaleString(i18n.language, {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
